import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  Badge,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  useColorModeValue,
  useToast,
  Grid,
  GridItem,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@/contexts/ThemeContext';
import { useTeamService } from '@/hooks/useTeamService';

// Icons
const MoreIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </svg>
);

const DeleteIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
  </svg>
);

const ContactIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
  </svg>
);

interface TeamContact {
  id: string;
  team_id: string;
  type: string;
  value: string;
  label?: string;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

interface TeamContactsTabProps {
  teamId: string;
  permissions: {
    can_view_team: boolean;
    can_manage_contacts: boolean;
    can_add_contact: boolean;
    can_update_contact: boolean;
    can_delete_contact: boolean;
  };
  isActive: boolean;
  onEditContact?: (contact: TeamContact) => void;
  onDeleteContact?: (contactId: string) => void;
}

export const TeamContactsTab: React.FC<TeamContactsTabProps> = ({
  teamId,
  permissions,
  isActive,
  onEditContact,
  onDeleteContact,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { colorSchemes } = useThemeContext();
  const teamService = useTeamService();

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.800');

  // State
  const [contacts, setContacts] = useState<TeamContact[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [hasLoaded, setHasLoaded] = useState(false);

  // Load contacts data only when tab becomes active
  const loadContacts = async () => {
    if (!teamId || !permissions.can_view_team) return;

    try {
      setIsLoading(true);
      setError('');
      
      const contactsData = await teamService.getTeamContacts(teamId);
      setContacts(contactsData || []);
      setHasLoaded(true);
    } catch (err: any) {
      console.error('Error loading team contacts:', err);
      setError(err.message || 'Failed to load team contacts');
      toast({
        title: t('common.error'),
        description: err.message || 'Failed to load team contacts',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Load data when tab becomes active
  useEffect(() => {
    if (isActive && !hasLoaded && permissions.can_view_team) {
      loadContacts();
    }
  }, [isActive, hasLoaded, permissions.can_view_team, teamId]);

  // Get contact type badge color
  const getContactTypeBadgeColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'email':
        return 'blue';
      case 'phone':
        return 'green';
      case 'website':
        return 'purple';
      case 'social':
        return 'pink';
      default:
        return 'gray';
    }
  };

  // Handle edit contact
  const handleEditContact = (contact: TeamContact) => {
    if (!permissions.can_update_contact) return;
    if (onEditContact) {
      onEditContact(contact);
    }
  };

  // Handle delete contact
  const handleDeleteContact = async (contact: TeamContact) => {
    if (!permissions.can_delete_contact) return;
    
    if (window.confirm(`Are you sure you want to delete this ${contact.type} contact?`)) {
      try {
        if (onDeleteContact) {
          await onDeleteContact(contact.id);
          await loadContacts(); // Reload after deletion
        }
      } catch (error) {
        console.error('Error deleting contact:', error);
      }
    }
  };

  // Permission check
  if (!permissions.can_view_team) {
    return (
      <Box textAlign="center" py={8}>
        <Alert status="warning">
          <AlertIcon />
          {t('teams.noPermissionToViewContacts')}
        </Alert>
      </Box>
    );
  }

  // Loading state
  if (isLoading && !hasLoaded) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="lg" />
        <Text>{t('teams.loadingContacts')}</Text>
      </VStack>
    );
  }

  // Error state
  if (error && !isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
        <Button mt={4} onClick={loadContacts} colorScheme={colorSchemes.primary}>
          {t('common.retry')}
        </Button>
      </Box>
    );
  }

  // Empty state
  if (!isLoading && contacts.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Text color={textColor} fontSize="lg">
          {t('teams.noContacts')}
        </Text>
        {permissions.can_add_contact && (
          <Text color={textColor} fontSize="sm" mt={2}>
            {t('teams.addFirstContact')}
          </Text>
        )}
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Header */}
      <HStack justify="space-between" align="center">
        <Text fontSize="lg" fontWeight="semibold">
          {t('teams.teamContacts')} ({contacts.length})
        </Text>
        <Button
          size="sm"
          onClick={loadContacts}
          isLoading={isLoading}
          colorScheme={colorSchemes.neutral}
          variant="outline"
        >
          {t('common.refresh')}
        </Button>
      </HStack>

      {/* Contacts Grid */}
      <Grid templateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={4}>
        {contacts.map((contact) => (
          <GridItem key={contact.id}>
            <Card
              bg={cardBg}
              borderWidth={1}
              borderColor={borderColor}
              transition="all 0.2s"
              _hover={{
                transform: 'translateY(-2px)',
                shadow: 'md',
                borderColor: `${colorSchemes.primary}.300`,
              }}
            >
              <CardBody>
                <VStack align="stretch" spacing={3}>
                  {/* Header with contact info and actions */}
                  <HStack justify="space-between" align="start">
                    <HStack spacing={3}>
                      <ContactIcon />
                      <VStack align="start" spacing={0}>
                        <HStack spacing={2}>
                          <Text fontWeight="semibold" fontSize="md">
                            {contact.label || contact.type}
                          </Text>
                          {contact.is_primary && (
                            <Badge colorScheme="blue" variant="solid" size="sm">
                              {t('teams.primary')}
                            </Badge>
                          )}
                        </HStack>
                        <Text fontSize="sm" color={textColor}>
                          {contact.value}
                        </Text>
                      </VStack>
                    </HStack>

                    {/* Actions Menu */}
                    {permissions.can_manage_contacts && (
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<MoreIcon />}
                          size="sm"
                          variant="ghost"
                        />
                        <MenuList>
                          {permissions.can_update_contact && (
                            <MenuItem
                              icon={<EditIcon />}
                              onClick={() => handleEditContact(contact)}
                            >
                              {t('teams.editContact')}
                            </MenuItem>
                          )}
                          {permissions.can_delete_contact && (
                            <MenuItem
                              icon={<DeleteIcon />}
                              color="red.500"
                              onClick={() => handleDeleteContact(contact)}
                            >
                              {t('teams.deleteContact')}
                            </MenuItem>
                          )}
                        </MenuList>
                      </Menu>
                    )}
                  </HStack>

                  {/* Contact Type */}
                  <HStack spacing={2}>
                    <Badge
                      colorScheme={getContactTypeBadgeColor(contact.type)}
                      variant="subtle"
                      size="sm"
                    >
                      {contact.type}
                    </Badge>
                  </HStack>

                  {/* Dates */}
                  <VStack align="stretch" spacing={1}>
                    <Text fontSize="xs" color={textColor}>
                      {t('teams.addedOn')}: {new Date(contact.created_at).toLocaleDateString()}
                    </Text>
                    {contact.updated_at !== contact.created_at && (
                      <Text fontSize="xs" color={textColor}>
                        {t('teams.updatedOn')}: {new Date(contact.updated_at).toLocaleDateString()}
                      </Text>
                    )}
                  </VStack>

                  {/* Action buttons */}
                  {permissions.can_manage_contacts && (
                    <HStack spacing={2} pt={2}>
                      {permissions.can_update_contact && (
                        <Button
                          size="sm"
                          colorScheme={colorSchemes.primary}
                          variant="outline"
                          leftIcon={<EditIcon />}
                          onClick={() => handleEditContact(contact)}
                          flex={1}
                        >
                          {t('teams.edit')}
                        </Button>
                      )}
                      {permissions.can_delete_contact && (
                        <Button
                          size="sm"
                          colorScheme="red"
                          variant="outline"
                          leftIcon={<DeleteIcon />}
                          onClick={() => handleDeleteContact(contact)}
                        >
                          {t('teams.delete')}
                        </Button>
                      )}
                    </HStack>
                  )}
                </VStack>
              </CardBody>
            </Card>
          </GridItem>
        ))}
      </Grid>
    </VStack>
  );
}; 